import { DrawingProperties, parsePinPropertyKey } from '@cadstrom/renderer';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { DrawingPropertyValue } from '@cadstrom/renderer';
import type { ViewportState } from '#src/utils/events.js';

/**
 * A single property-value list in serialisable form (arrays instead of Maps/Sets).
 * @param 0 - The drawing property type (e.g. DrawingProperties.ObjectId)
 * @param 1 - Sorted list of values for that property
 */
export type SerializableSelectionEntry = readonly [DrawingProperties, readonly DrawingPropertyValue[]];

/** The full selection state in a serialisable form. */
export type SerializableSelection = readonly SerializableSelectionEntry[];

export interface SelectionHistoryEntry {
	readonly id: string;
	readonly designId: string | null;
	readonly instanceId?: string;
	readonly selection: SerializableSelection;
	/** Viewport captured at push time. Never mutated — serves as the guaranteed fallback for restoration. */
	readonly viewport: ViewportState | null;
	/**
	 * Set ~2 s after the camera last moved while this was the latest entry.
	 * A debounced snapshot that captures "where the user recently settled", even for short dwells.
	 */
	readonly delayedViewport: ViewportState | null;
	/**
	 * Set ~4 s after the camera last moved while this was the latest entry.
	 * Preferred over `delayedViewport` when restoring — represents a more deliberate resting point.
	 */
	readonly restingViewport: ViewportState | null;
	readonly label: string;
	/** Unix timestamp (ms) when this entry was recorded. */
	readonly timestamp: number;
}

/** The navigation stack for a single project. */
export interface ProjectHistory {
	readonly entries: readonly SelectionHistoryEntry[];
	readonly currentIndex: number;
}

export const EMPTY_HISTORY: ProjectHistory = { entries: [], currentIndex: -1 };

interface SelectionHistoryStoreState {
	/** Per-project navigation stacks, keyed by project ID. */
	readonly histories: Readonly<Record<string, ProjectHistory>>;
	readonly isNavigating: boolean;
}

interface SelectionHistoryStoreActions {
	/** Push a new entry into the given project's stack, truncating any forward history. */
	push: (
		projectId: string,
		entry: Omit<SelectionHistoryEntry, 'id' | 'timestamp' | 'delayedViewport' | 'restingViewport'>,
	) => void;
	/** Set the delayed viewport of the current entry in the given project's stack in-place. */
	updateCurrentDelayedViewport: (projectId: string, viewport: ViewportState) => void;
	/** Set the resting viewport of the current entry in the given project's stack in-place. */
	updateCurrentRestingViewport: (projectId: string, viewport: ViewportState) => void;
	/** Update the selection and label of the current entry in the given project's stack in-place. */
	updateCurrentSelection: (projectId: string, selection: SerializableSelection, label: string) => void;
	/** Jump to a specific index in the given project's stack and return the entry, or null if out of range. */
	navigateTo: (projectId: string, index: number) => SelectionHistoryEntry | null;
	/** Set the navigating flag to suppress recording while restoring history state. */
	setIsNavigating: (value: boolean) => void;
}

export type SelectionHistoryStore = SelectionHistoryStoreState & SelectionHistoryStoreActions;

const MAX_HISTORY_SIZE = 50;
let nextId = 1;

/**
 * Returns the updated `histories` slice after applying a mutation to one project's stack.
 * @param state - Current store state.
 * @param projectId - The project to mutate.
 * @param updater - Pure function transforming the project's history.
 * @returns Partial state update containing the new `histories` map.
 */
function applyToProject(
	state: SelectionHistoryStoreState,
	projectId: string,
	updater: (history: ProjectHistory) => ProjectHistory,
): Pick<SelectionHistoryStoreState, 'histories'> {
	const current = state.histories[projectId] ?? EMPTY_HISTORY;
	return { histories: { ...state.histories, [projectId]: updater(current) } };
}

export const useSelectionHistoryStore = create<SelectionHistoryStore>()(
	persist(
		(set, get) => ({
			histories: {},
			isNavigating: false,

			push: (projectId, entry) => {
				set((state) =>
					applyToProject(state, projectId, ({ entries, currentIndex }) => {
						const newEntry: SelectionHistoryEntry = {
							...entry,
							id: String(nextId++),
							timestamp: Date.now(),
							delayedViewport: null,
							restingViewport: null,
						};
						const base = entries.slice(0, currentIndex + 1);
						const updated = [...base, newEntry].slice(-MAX_HISTORY_SIZE);
						return { entries: updated, currentIndex: updated.length - 1 };
					}),
				);
			},

			updateCurrentDelayedViewport: (projectId, delayedViewport) => {
				set((state) =>
					applyToProject(state, projectId, (history) => {
						if (history.currentIndex < 0 || history.currentIndex >= history.entries.length) {
							return history;
						}
						const entries = [...history.entries];
						entries[history.currentIndex] = { ...entries[history.currentIndex]!, delayedViewport };
						return { ...history, entries };
					}),
				);
			},

			updateCurrentRestingViewport: (projectId, restingViewport) => {
				set((state) =>
					applyToProject(state, projectId, (history) => {
						if (history.currentIndex < 0 || history.currentIndex >= history.entries.length) {
							return history;
						}
						const entries = [...history.entries];
						entries[history.currentIndex] = { ...entries[history.currentIndex]!, restingViewport };
						return { ...history, entries };
					}),
				);
			},

			updateCurrentSelection: (projectId, selection, label) => {
				set((state) =>
					applyToProject(state, projectId, (history) => {
						if (history.currentIndex < 0 || history.currentIndex >= history.entries.length) {
							return history;
						}
						const entries = [...history.entries];
						entries[history.currentIndex] = { ...entries[history.currentIndex]!, selection, label };
						return { ...history, entries };
					}),
				);
			},

			navigateTo: (projectId, index) => {
				const history = get().histories[projectId] ?? EMPTY_HISTORY;
				if (index < 0 || index >= history.entries.length) {
					return null;
				}
				const entry = history.entries[index] ?? null;
				set((state) => applyToProject(state, projectId, (h) => ({ ...h, currentIndex: index })));
				return entry;
			},

			setIsNavigating: (value) => set({ isNavigating: value }),
		}),
		{
			name: 'cad-renderer-selection-history',
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({ histories: state.histories }),
			onRehydrateStorage: () => (state) => {
				if (!state) {
					return;
				}
				const allEntries = Object.values(state.histories).flatMap((h) => h.entries);
				if (allEntries.length === 0) {
					return;
				}
				// Advance the ID counter past any IDs restored from storage so new entries never
				// collide with persisted ones.
				const maxId = Math.max(...allEntries.map((e) => parseInt(e.id, 10)).filter(Number.isFinite));
				if (maxId >= nextId) {
					nextId = maxId + 1;
				}
			},
		},
	),
);

/**
 * Converts an InteractionState selection Map into the serialisable array form.
 * @param selection - The raw Map<DrawingProperties, Set<DrawingPropertyValue>>, or undefined.
 * @returns Sorted, serialisable selection array.
 */
export function serializeSelection(
	selection: Map<DrawingProperties, Set<DrawingPropertyValue>> | undefined,
): SerializableSelection {
	if (!selection || selection.size === 0) {
		return [];
	}
	return [...selection.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([prop, values]) => [prop, [...values].sort()] as const);
}

/**
 * Returns true if two serialised selections contain exactly the same entries.
 * @param a - First selection.
 * @param b - Second selection.
 */
export function selectionsEqual(a: SerializableSelection, b: SerializableSelection): boolean {
	if (a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		const [aProp, aVals] = a[i]!;
		const [bProp, bVals] = b[i]!;
		if (aProp !== bProp || aVals.length !== bVals.length) {
			return false;
		}
		for (let j = 0; j < aVals.length; j++) {
			if (aVals[j] !== bVals[j]) {
				return false;
			}
		}
	}
	return true;
}

/**
 * Returns true if `next` is a strict superset of `prev` for the same design.
 * Used to detect additive (Shift+click) selection changes.
 * @param prev - The previously recorded selection.
 * @param next - The new selection to compare against.
 */
export function isAdditiveSelection(prev: SerializableSelection, next: SerializableSelection): boolean {
	if (next.length === 0) {
		return false;
	}

	const prevMap = new Map(prev.map(([p, vs]) => [p, new Set(vs)]));
	const nextMap = new Map(next.map(([p, vs]) => [p, new Set(vs)]));

	// Every item in prev must still be present in next
	for (const [prop, values] of prevMap) {
		const nextValues = nextMap.get(prop);
		if (!nextValues) {
			return false;
		}
		for (const v of values) {
			if (!nextValues.has(v)) {
				return false;
			}
		}
	}

	// next must contain strictly more items than prev
	let prevCount = 0;
	for (const values of prevMap.values()) {
		prevCount += values.size;
	}
	let nextCount = 0;
	for (const values of nextMap.values()) {
		nextCount += values.size;
	}
	return nextCount > prevCount;
}

function formatItemList(items: readonly DrawingPropertyValue[], isPanCheckpoint: boolean): string {
	const displayItems = items.slice(0, 3).join(', ');
	const ellipsis = items.length > 3 ? ', …' : '';
	return isPanCheckpoint ? `Viewing ${displayItems}${ellipsis}` : `Selected ${displayItems}${ellipsis}`;
}

/**
 * Generates a human-readable label for a history entry.
 * @param selection - Serialised selection for the entry.
 * @param designId - The design ID the entry was recorded on.
 * @param prevDesignId - The design ID of the previous history entry (used to detect navigation).
 * @param designs - All schematic designs in the project (for looking up sheet names).
 * @returns Display label such as "Selected U1", "Navigated to Sheet 3", etc.
 */
export function generateLabel(
	selection: SerializableSelection,
	designId: string | null,
	prevDesignId: string | null | undefined,
	designs: ReadonlyArray<{
		readonly designId: string;
		readonly designName?: string | null;
		readonly sheetIndex: number;
	}>,
	options?: { isPanCheckpoint?: boolean },
): string {
	const isPanCheckpoint = options?.isPanCheckpoint ?? false;
	const design = designId ? designs.find((d) => d.designId === designId) : null;
	const sheetName = design?.designName ?? (design ? `Sheet ${design.sheetIndex + 1}` : null);
	const designChanged = designId !== prevDesignId;

	const objectIds = selection.find(([p]) => p === DrawingProperties.ObjectId)?.[1] ?? [];
	const nets = selection.find(([p]) => p === DrawingProperties.Net)?.[1] ?? [];
	const pins = selection.find(([p]) => p === DrawingProperties.Pin)?.[1] ?? [];
	const pads = selection.find(([p]) => p === DrawingProperties.Pad)?.[1] ?? [];
	const pinOrPadEntries = pins.length > 0 ? pins : pads;
	const pinOrPadKind = pins.length > 0 ? 'pin' : 'pad';
	const formattedPinOrPad = pinOrPadEntries.map((v) => {
		const parsed = parsePinPropertyKey(String(v));
		return parsed ? `${parsed.moduleRef} ${pinOrPadKind} ${parsed.pinRef}` : String(v);
	});

	let label: string;

	if (objectIds.length > 0) {
		label = formatItemList(objectIds, isPanCheckpoint);
	} else if (nets.length > 0) {
		label = formatItemList(nets, isPanCheckpoint);
	} else if (formattedPinOrPad.length > 0) {
		label = formatItemList(formattedPinOrPad, isPanCheckpoint);
	} else if (sheetName !== null && designChanged) {
		label = isPanCheckpoint ? `Viewing ${sheetName}` : `Navigated to ${sheetName}`;
	} else {
		label = isPanCheckpoint ? 'Viewport' : 'Selection cleared';
	}

	if (
		designChanged &&
		sheetName !== null &&
		(objectIds.length > 0 || nets.length > 0 || formattedPinOrPad.length > 0)
	) {
		label += ` on ${sheetName}`;
	}

	return label;
}
